'use client';

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Alert, Box, Typography } from '@mui/material';
import FeaturedShowrooms from '@/components/showrooms/FeaturedShowrooms';
import { MARKETING } from '@/components/website/Marketing/marketing-tokens';
import {
  indexEntriesFromSections,
  resolvedImageAlt,
  resolvedImageUrl,
  sectionImageValue,
  STUDIO_CMS_SLUG,
} from '@/components/website/MarketingPage/marketing-page-utils';
import StudioAboutSections from '@/components/website/Studio/StudioAboutSections';
import StudioIntro from '@/components/website/Studio/StudioIntro';
import WebsiteInteriorScrollLayout from './WebsiteInteriorScrollLayout';
import { renderTradeSection } from './TradePage';

function mapCmsShowroom(showroom) {
  return {
    id: showroom.id,
    city: showroom.city || '',
    name: showroom.name || '',
    region: showroom.region || '',
    mapHref: showroom.mapHref || null,
    exteriorImage: resolvedImageUrl(showroom.imageAssetId) || '',
    exteriorAlt: showroom.imageAlt || resolvedImageAlt(showroom.imageAssetId, ''),
    interiorSlides: (showroom.interiorSlides || [])
      .map(slide => ({
        id: slide.id,
        imageUrl: resolvedImageUrl(slide.imageAssetId) || '',
        alt: slide.alt || resolvedImageAlt(slide.imageAssetId, ''),
        durationMs:
          typeof slide.durationMs === 'number' && slide.durationMs >= 1 ? slide.durationMs : 6000,
      }))
      .filter(slide => slide.imageUrl),
  };
}

function mapIndexedToStudioSection(section) {
  const data = section?.data || {};
  const imageValue = sectionImageValue(data);
  return {
    id: section.id || data.indexId,
    eyebrow: data.eyebrow || '',
    title: data.heading || '',
    body: data.body || '',
    imageSrc: resolvedImageUrl(imageValue) || '',
    imageAlt: data.imageAlt || resolvedImageAlt(imageValue, data.heading || ''),
    imagePosition: data.imageSide === 'right' ? 'right' : 'left',
  };
}

function heroScrollTargetId(sections) {
  const intro = (sections ?? []).find(section => section.type === 'intro_quote');
  if (intro?.id) return intro.id;
  const indexed = indexEntriesFromSections(sections);
  if (indexed[0]?.id) return indexed[0].id;
  const firstNonHero = (sections ?? []).find(section => section.type !== 'hero');
  return firstNonHero?.data?.indexId || firstNonHero?.id;
}

function renderStudioSections(sections) {
  const scrollTargetId = heroScrollTargetId(sections);
  const nodes = [];
  const indexedBatch = [];

  const flushIndexed = () => {
    if (!indexedBatch.length) return;
    nodes.push(
      <StudioAboutSections key={`indexed-${indexedBatch[0].id}`} sections={[...indexedBatch]} />,
    );
    indexedBatch.length = 0;
  };

  (sections ?? []).forEach(section => {
    if (!section?.type) return;
    if (section.type === 'trade_intro') {
      flushIndexed();
      const data = section.data || {};
      nodes.push(
        <StudioIntro
          key={section.id}
          id={section.id}
          title={data.title || ''}
          body={data.body || ''}
        />,
      );
      return;
    }
    if (section.type === 'studio_showrooms') {
      flushIndexed();
      const data = section.data || {};
      const showrooms = (data.showrooms || [])
        .map(mapCmsShowroom)
        .filter(showroom => showroom.exteriorImage);
      nodes.push(
        <FeaturedShowrooms
          key={section.id}
          id={section.id}
          heading={data.heading || 'Featured Showrooms'}
          showrooms={showrooms}
        />,
      );
      return;
    }
    if (section.type === 'indexed_text_image') {
      indexedBatch.push(mapIndexedToStudioSection(section));
      return;
    }
    flushIndexed();
    const rendered = renderTradeSection(section, scrollTargetId);
    if (rendered) nodes.push(rendered);
  });
  flushIndexed();
  return nodes;
}

export default function StudioPage({
  fontClassName,
  page: initialPage = null,
  preview = false,
  slug = STUDIO_CMS_SLUG,
}) {
  const [page, setPage] = useState(initialPage);
  const [previewError, setPreviewError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(preview);

  useEffect(() => {
    if (!preview) return undefined;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError('');
    axios
      .get(`/api/v2/gallery/marketing-pages/${encodeURIComponent(slug)}/preview/`, {
        withCredentials: true,
      })
      .then(({ data }) => {
        if (!cancelled) {
          setPage(data);
          setPreviewLoading(false);
        }
      })
      .catch(error => {
        if (cancelled) return;
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          setPreviewError('Draft preview requires a signed-in CMS editor.');
        } else if (status === 404) {
          setPreviewError('Draft preview is not available for this page.');
        } else {
          setPreviewError('Unable to load draft preview.');
        }
        setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [preview, slug]);

  const sections = page?.sections ?? [];

  const content = (() => {
    if (previewLoading) {
      return (
        <Typography sx={{ px: 3, py: 8 }} color="text.secondary">
          Loading draft preview…
        </Typography>
      );
    }
    if (previewError) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {previewError}
        </Alert>
      );
    }
    if (!page) {
      return <Typography sx={{ px: 3, py: 8 }}>Page not found.</Typography>;
    }
    return renderStudioSections(sections);
  })();

  return (
    <WebsiteInteriorScrollLayout
      contentSx={{
        bgcolor: MARKETING.canvas,
        color: MARKETING.text,
        p: 0,
      }}
    >
      <Box
        className={fontClassName}
        sx={{
          bgcolor: MARKETING.canvas,
          color: MARKETING.text,
          overflowX: 'clip',
        }}
      >
        {preview ? (
          <Alert
            severity="warning"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
              borderRadius: 0,
            }}
          >
            Draft preview — unpublished changes. Not visible to the public.
          </Alert>
        ) : null}
        {content}
      </Box>
    </WebsiteInteriorScrollLayout>
  );
}

StudioPage.propTypes = {
  fontClassName: PropTypes.string,
  page: PropTypes.object,
  preview: PropTypes.bool,
  slug: PropTypes.string,
};
